import { useEffect, useRef, useState } from 'react';
import fragment from './liquidMetal.frag.glsl?raw';
import Mark from '../../components/Mark';

const VERTEX = 'attribute vec2 aVertexPosition;void main(){gl_Position=vec4(aVertexPosition,0.0,1.0);}';
const SIZE = 128;

const CHROME = {
  speed: 0.4,
  iterations: 15,
  scale: 3.12,
  dotFactor: 0.04,
  dotMultiplier: 0.21,
  vOffset: 5.1,
  intensityFactor: 0.07,
  expFactor: 0.2,
  colorFactors: [1.1, 0.7, 0.9] as const,
  colorShift: 0.9,
  noiseIntensity: 4,
  logoInteractStrength: 0.4,
};

function markTexture(): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = c.height = SIZE;
  const g = c.getContext('2d')!;
  const k = SIZE / 32;
  g.lineWidth = 2.6 * k;
  g.strokeStyle = g.fillStyle = '#ffffff';
  const tile = (x: number, y: number, fill: boolean) => {
    g.beginPath();
    g.roundRect(x * k, y * k, 12 * k, 12 * k, 3.5 * k);
    if (fill) g.fill();
    g.stroke();
  };
  tile(2, 2, false);
  tile(18, 2, false);
  tile(2, 18, false);
  tile(18, 18, true);
  return c;
}

function start(canvas: HTMLCanvasElement, moving: boolean): (() => void) | null {
  canvas.width = canvas.height = SIZE;
  const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false, antialias: true });
  if (!gl) return null;
  const shader = (type: number, src: string) => {
    const s = gl.createShader(type)!;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
  };
  const vs = shader(gl.VERTEX_SHADER, VERTEX);
  const fs = shader(gl.FRAGMENT_SHADER, fragment);
  if (!vs || !fs) return null;
  const program = gl.createProgram()!;
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
  gl.useProgram(program);

  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const pos = gl.getAttribLocation(program, 'aVertexPosition');
  gl.enableVertexAttribArray(pos);
  gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);

  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, markTexture());

  const u = (name: string) => gl.getUniformLocation(program, name);
  gl.uniform2f(u('u_resolution'), SIZE, SIZE);
  gl.uniform1f(u('u_speed'), CHROME.speed);
  gl.uniform1f(u('u_iterations'), CHROME.iterations);
  gl.uniform1f(u('u_scale'), CHROME.scale);
  gl.uniform1f(u('u_dotFactor'), CHROME.dotFactor);
  gl.uniform1f(u('u_dotMultiplier'), CHROME.dotMultiplier);
  gl.uniform1f(u('u_vOffset'), CHROME.vOffset);
  gl.uniform1f(u('u_intensityFactor'), CHROME.intensityFactor);
  gl.uniform1f(u('u_expFactor'), CHROME.expFactor);
  gl.uniform3f(u('u_colorFactors'), ...CHROME.colorFactors);
  gl.uniform1f(u('u_colorShift'), CHROME.colorShift);
  gl.uniform1f(u('u_noiseIntensity'), CHROME.noiseIntensity);
  gl.uniform1i(u('u_logoTexture'), 0);
  gl.uniform1f(u('u_logoOpacity'), 1);
  gl.uniform1f(u('u_logoScale'), 0.92);
  gl.uniform1f(u('u_logoAspectRatio'), 1);
  gl.uniform1f(u('u_logoInteractStrength'), CHROME.logoInteractStrength);
  gl.uniform1i(u('u_logoBlendMode'), 0);
  const time = u('u_time');

  gl.viewport(0, 0, SIZE, SIZE);
  gl.clearColor(0, 0, 0, 0);
  const draw = (t: number) => {
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.uniform1f(time, t);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };

  let raf = 0;
  const t0 = performance.now();
  const loop = () => {
    if (!document.hidden) draw((performance.now() - t0) / 1000);
    raf = requestAnimationFrame(loop);
  };
  if (moving) loop(); else draw(6);

  return () => {
    cancelAnimationFrame(raf);
    gl.deleteTexture(texture);
    gl.deleteBuffer(buffer);
    gl.deleteProgram(program);
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  };
}

export default function LiquidMark({ moving }: { moving: boolean }) {
  const host = useRef<HTMLSpanElement | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const canvas = document.createElement('canvas');
    const stop = start(canvas, moving);
    if (!stop) { setFailed(true); return; }
    el.appendChild(canvas);
    return () => { stop(); canvas.remove(); };
  }, [moving]);

  if (failed) return <Mark className="brand-mark" />;
  return <span ref={host} className="lq-mark brand-mark" aria-hidden="true" />;
}
